import * as Sentry from "@sentry/react";
import { onCLS, onINP, onLCP, onTTFB, type Metric } from "web-vitals";
import { env } from "@infra/implementations/config/env";

/**
 * Default console reporter with color-coded output based on rating.
 */
function consoleReporter(metric: Metric) {
	const rating = metric.rating;
	const color = rating === "good" ? "green" : rating === "needs-improvement" ? "orange" : "red";
	console.log(
		`%c[Web Vital] ${metric.name}: ${metric.value.toFixed(2)} (${rating})`,
		`color: ${color}; font-weight: bold`,
	);
}

/**
 * Send Web Vitals metrics to Sentry.
 * - Adds breadcrumb for all metrics
 * - Captures message for poor ratings to alert on performance issues
 */
function sentryReporter(metric: Metric) {
	// Add breadcrumb for all Web Vitals
	Sentry.addBreadcrumb({
		category: "web-vital",
		message: `${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`,
		level: metric.rating === "good" ? "info" : metric.rating === "needs-improvement" ? "warning" : "error",
		data: {
			name: metric.name,
			value: metric.value,
			rating: metric.rating,
			navigationType: metric.navigationType,
			id: metric.id,
		},
	});

	// Capture message for poor metrics to create alerts
	if (metric.rating === "poor") {
		Sentry.captureMessage(`Poor Web Vital: ${metric.name}`, {
			level: "warning",
			tags: {
				webVital: metric.name,
				rating: metric.rating,
			},
			extra: {
				value: metric.value,
				navigationType: metric.navigationType,
				entries: metric.entries,
			},
		});
	}
}

export type WebVitalsReporter = "console" | "sentry" | "both" | ((metric: Metric) => void);

/**
 * Initialize Web Vitals performance monitoring.
 *
 * @param reporter - How to report metrics:
 *   - "console": Log to console with color-coded ratings (default in dev)
 *   - "sentry": Send to Sentry as custom metrics
 *   - "both": Log to console AND send to Sentry
 *   - Custom function: Your own reporter
 */
export function initPerformanceMonitoring(reporter: WebVitalsReporter = "console") {
	// In production without VITE_PERF_MONITOR, only run if using Sentry
	const isProduction = !env.isDev;
	const perfMonitorEnabled = env.perfMonitor;

	if (isProduction && !perfMonitorEnabled && reporter === "console") {
		return;
	}

	let report: (metric: Metric) => void;

	if (typeof reporter === "function") {
		report = reporter;
	} else if (reporter === "sentry") {
		report = sentryReporter;
	} else if (reporter === "both") {
		report = (metric) => {
			consoleReporter(metric);
			sentryReporter(metric);
		};
	} else {
		report = consoleReporter;
	}

	onCLS(report);
	onINP(report);
	onLCP(report);
	onTTFB(report);
}
